/**
 * useCommitFeedbackToasts — surfaces commit-FAILURE toasts for the Visual
 * Query Editor.
 *
 * `commitEdit` records a `commitFeedback` payload on the vqeStore after each
 * apply→emit attempt. This hook subscribes to that field and toasts every NEW
 * failure payload — failures always deserve a notice. Successes are not
 * toasted: the surface that made the edit shows its own inline "Edit applied ·
 * Undo" status (`useEditAppliedStatus` + `VqeEditStatus`), which sits in that
 * surface's header/footer instead of floating over the results grid.
 *
 * Mount once near the top of each surface (the Visual view's `VqeStatementView`,
 * the compact popover `CompactVqePopover`) — both can be mounted at the same
 * time (one tab in Visual plus a compact popover open elsewhere), and both
 * observe the SAME `vqeStore.commitFeedback` stream. The last-seen guard is
 * therefore module-level (not a per-instance `useRef`): each payload is a
 * fresh object (the `at` timestamp differs), so whichever mounted instance's
 * effect runs first claims it and toasts; every other concurrently-mounted
 * instance sees the identity already claimed and stays silent. A per-instance
 * ref would NOT be enough here — two instances would each see the payload as
 * "new" and double-toast the same failure.
 *
 * The toast wrapper (`components/ui/toaster`) is the app-root toast provider;
 * copy comes from `vqeCopy.toast.*` so no user-facing string is hardcoded here.
 */
import { useEffect } from 'react';

import { toast } from '../../../../components/ui/toaster';
import { useVqeStore } from '../store/vqeStore';
import type { VqeCommitFeedback } from '../store/vqeStore';
import { vqeCopy } from './vqeCopy';

// Module-level (process-wide, not per-instance) — see docblock above for why
// this can't live inside a `useRef`.
let lastToastedFeedback: VqeCommitFeedback | null = null;

export function useCommitFeedbackToasts(): void {
  const commitFeedback = useVqeStore((s) => s.commitFeedback);

  useEffect(() => {
    if (!commitFeedback) return;
    // Identity guard — the same payload object must fire exactly once even if
    // multiple hook instances are mounted concurrently, or an unrelated store
    // change re-runs this effect.
    if (lastToastedFeedback === commitFeedback) return;
    lastToastedFeedback = commitFeedback;

    if (commitFeedback.kind !== 'failure') return;
    toast.error({
      title: vqeCopy.toast.failureTitle,
      description: vqeCopy.toast.commitFailure(commitFeedback.message ?? ''),
    });
  }, [commitFeedback]);
}
